import React, { useContext } from 'react';
import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Dinero from 'dinero.js';
import ICartModal from '../interfaces/ICartModal';
import useMediaQuery from '../../../utils/hooks/mediaQuery.hook';
import { OrderContext } from '../../../context/OrderContext';
import ArticlesPreview from '../../../components/ArticlesPreview';
import { CampaignContext } from '../../../context/CampaignWrapper';
import TooltipWrapper from '../../../components/TooltipWrapper';

import BasketSmImage from '../../../assets/images/basket-sm.svg';
import BasketImage from '../../../assets/images/basket.svg';
import { ReactComponent as BasketIcon } from '../../../assets/icons/basket.svg';
import AcceptDonations from './AcceptDonations';

const EmptyCartBody = () => {
  const { isMobile } = useMediaQuery();
  const [translate] = useTranslation();

  const image = isMobile ? BasketSmImage : BasketImage;

  return (
    <div className="text-center align-self-center">
      <img src={image} alt="basket-img" />
      <div className="my-4">
        <p className="fs-5 m-0">
          {translate('COMPONENTS.CART.EMPTY_CART')}
        </p>
        <p className="text-gray">
          {translate('COMPONENTS.CART.EMPTY_CART_HINT')}
        </p>
        <AcceptDonations className="text-gray" />
      </div>
    </div>
  );
};

const CartModal = ({ isOpen, setIsOpen }: ICartModal) => {
  const { campaign: { minimumTotalArticleCount, minimumOrderValue, articles } } = useContext(CampaignContext);
  const { order: { quantities }, articleCount } = useContext(OrderContext);
  const navigate = useNavigate();
  const [translate] = useTranslation();
  const { search } = useLocation();
  const { slug } = useParams();

  const totalPrice = articles.reduce((sum, { _id, price }) => sum + ((quantities[_id] || 0) * price), 0);

  const isCartEmpty = !articleCount;
  const cartTotalItemsHeader = articleCount ? `(${articleCount})` : '';

  const iconStyle = { verticalAlign: 'middle' };
  const cartBodyClass = `cart-body ${isCartEmpty ? 'd-flex align-items-stretch justify-content-center' : ''}`;

  const toggleOpen = () => {
    setIsOpen((state) => !state);
  };

  const navigateToCheckout = () => {
    navigate(`/${slug}/checkout${search}`);
  };

  const getValidationMessage = () => {
    if (minimumTotalArticleCount && articleCount < minimumTotalArticleCount) {
      return translate(
        'COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.MINIMUM_ITEMS_PER_ORDER',
        { count: minimumTotalArticleCount },
      );
    }

    if (totalPrice < minimumOrderValue) {
      return translate(
        'COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.MINIMUM_ORDER_VALUE',
        { value: Dinero({ amount: minimumOrderValue }).toFormat() },
      );
    }

    return '';
  };

  const validationMessage = getValidationMessage();
  const isButtonDisabled = !!validationMessage;

  return (
    <Modal className="cart px-0" toggle={toggleOpen} isOpen={isOpen} scrollable>
      <ModalHeader className="cart-header" toggle={toggleOpen}>
        <div className="align-self-center">
          <BasketIcon fill="#32325D" style={iconStyle} />
          {' '}
          {translate('COMPONENTS.CART.SHOPPING_CART_HEADER')}
          {' '}
          {cartTotalItemsHeader}
        </div>
      </ModalHeader>
      <ModalBody className={cartBodyClass}>
        {isCartEmpty ? (
          <EmptyCartBody />
        ) : (
          <div className="mx-3">
            <ArticlesPreview />
          </div>
        )}
      </ModalBody>
      {!isCartEmpty && (
        <ModalFooter className="cart-footer px-0">
          <div className="modal-footer-button">
            <Button className="text-primary fw-bold w-100" onClick={toggleOpen}>
              {translate('COMPONENTS.CART.CONTINUE_SHOPPING')}
            </Button>
          </div>
          <TooltipWrapper
            tooltip={validationMessage}
            className="modal-footer-button"
          >
            <Button
              color="primary"
              className="w-100"
              onClick={navigateToCheckout}
              disabled={isButtonDisabled}
            >
              {translate('COMPONENTS.CART.CHECKOUT')}
            </Button>
          </TooltipWrapper>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default CartModal;
